import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { showError, showInfo } from "../utils/toast";
import "../sass/components/modalResultadoRetornoProjeto.scss";

const RESULTADOS = [
  {
    value: "CONFIRMADO",
    label: "Cliente confirmou",
    description: "Encerra o chamado de retorno e conclui o projeto.",
    tone: "success",
  },
  {
    value: "REAJUSTE",
    label: "Problema continua",
    description: "Encerra o retorno e devolve o projeto para reajuste.",
    tone: "danger",
  },
  {
    value: "SEM_CONTATO",
    label: "Sem contato",
    description: "Registra a tentativa e mantém o chamado aberto.",
    tone: "warning",
  },
];

const norm = (valor) => (valor == null ? "" : String(valor).trim());

const ModalResultadoRetornoProjeto = ({
  isOpen,
  onClose,
  chamado,
  funcionario,
  codFunc,
  onSuccess,
}) => {
  const [resultado, setResultado] = useState("CONFIRMADO");
  const [descricao, setDescricao] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState("");

  const idChamado = Number(chamado?.id ?? chamado?.ID ?? 0);
  const idProjeto =
    chamado?.ID_PROJETO_ORIGEM ?? chamado?.idProjetoOrigem ?? null;

  const opcaoAtual = useMemo(
    () => RESULTADOS.find((item) => item.value === resultado),
    [resultado],
  );

  useEffect(() => {
    if (!isOpen) return;
    setResultado("CONFIRMADO");
    setDescricao("");
    setErro("");
  }, [isOpen, idChamado]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !enviando) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, enviando, onClose]);

  if (!isOpen) return null;

  const enviar = async (event) => {
    event.preventDefault();
    if (enviando) return;

    if (!idChamado) {
      setErro("Chamado de retorno inválido.");
      return;
    }

    if (!norm(descricao)) {
      setErro("Descreva o contato realizado ou a tentativa de contato.");
      return;
    }

    setEnviando(true);
    setErro("");

    try {
      const { data } = await api.post(
        `/chamados/${idChamado}/resultado-retorno-projeto`,
        {
          resultado,
          descricao: norm(descricao),
          funcionario: norm(funcionario),
          codFunc: norm(codFunc),
        },
      );

      showInfo(data?.message || "Resultado registrado com sucesso.");
      await onSuccess?.(data);
      onClose?.();
    } catch (error) {
      const mensagem =
        error?.response?.data?.message ||
        error?.message ||
        "Falha ao registrar o resultado do retorno.";
      setErro(mensagem);
      showError(mensagem);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="mrr-backdrop" onMouseDown={onClose}>
      <section
        className="mrr-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mrr-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="mrr-header">
          <div>
            <span className="mrr-eyebrow">Validação com o cliente</span>
            <h2 id="mrr-title">Registrar retorno do projeto</h2>
            <p>
              Chamado #{idChamado} · Projeto #{idProjeto || "—"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={enviando}
            aria-label="Fechar"
          >
            ×
          </button>
        </header>

        <form onSubmit={enviar}>
          <div className="mrr-options" role="radiogroup">
            {RESULTADOS.map((item) => (
              <label
                key={item.value}
                className={`mrr-option ${item.tone} ${
                  resultado === item.value ? "selected" : ""
                }`}
              >
                <input
                  type="radio"
                  name="resultado-retorno"
                  value={item.value}
                  checked={resultado === item.value}
                  onChange={(event) => {
                    setResultado(event.target.value);
                    setErro("");
                  }}
                />
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.description}</small>
                </span>
              </label>
            ))}
          </div>

          <div className={`mrr-consequence ${opcaoAtual?.tone || ""}`}>
            <strong>Consequência</strong>
            <span>{opcaoAtual?.description}</span>
          </div>

          <label className="mrr-field">
            <span>Descrição obrigatória</span>
            <textarea
              rows={5}
              maxLength={2000}
              value={descricao}
              onChange={(event) => {
                setDescricao(event.target.value);
                setErro("");
              }}
              placeholder={
                resultado === "SEM_CONTATO"
                  ? "Informe horário, canal tentado e próxima ação."
                  : "Registre o que foi validado com o cliente."
              }
            />
          </label>

          {erro ? <div className="mrr-error">{erro}</div> : null}

          <footer className="mrr-footer">
            <button
              type="button"
              className="secondary"
              onClick={onClose}
              disabled={enviando}
            >
              Cancelar
            </button>
            <button type="submit" className="primary" disabled={enviando}>
              {enviando ? "Registrando..." : "Confirmar resultado"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
};

export default ModalResultadoRetornoProjeto;
